import api from '../api';

export const getLocation = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({ error: 'Location not supported on this device' });
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
    (e) => resolve({ error: e.code === 1 ? 'Location permission denied' : 'Location unavailable' }),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
  );
});

export const mapLink = (loc) => (loc && loc.lat != null ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}` : null);

const pad = (n) => String(n).padStart(2, '0');
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
let agencyTz;
export const setAgencyTz = (tz) => { agencyTz = tz; };
export const agencyToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: agencyTz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
export const agencyDay = (iso) => new Intl.DateTimeFormat('en-CA', { timeZone: agencyTz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: agencyTz }) : '—');
export const fmtDay = (d) => d.toLocaleDateString([], { weekday: 'short', month: '2-digit', day: '2-digit' });
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: agencyTz }) : '');
export const hrs = (h) => (h ? h.toFixed(2) : '0.00');
// datetime-local input value <-> ISO with zone
export const toLocalInput = (iso) => { if (!iso) return ''; const d = new Date(iso); return `${dayKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
export const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

export const weekStart = (d = new Date()) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - x.getDay()); return x; };
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export const fetchEntries = async (start, end, staffId) => (await api.get('/timeclock/entries', { params: { start: start.toISOString(), end: end.toISOString(), ...(staffId ? { staffId } : {}) } })).data.entries;

const merged = (ranges) => {
  const s = ranges.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
  const out = [];
  s.forEach(([a, b]) => { const last = out[out.length - 1]; if (last && a <= last[1]) last[1] = Math.max(last[1], b); else out.push([a, b]); });
  return out.reduce((t, [a, b]) => t + (b - a) / 3600000, 0);
};

// Paid hours per day = shift and visit times combined (overlaps counted once); California overtime estimate
export const summarize = (entries, days) => {
  const now = Date.now();
  const perDay = days.map((d) => {
    const k = dayKey(d);
    const rows = entries.filter((e) => agencyDay(e.clockIn) === k);
    const span = (e) => { const a = new Date(e.clockIn).getTime(); return [a, e.clockOut ? new Date(e.clockOut).getTime() : Math.min(now, new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime(), a + 16 * 3600000)]; };
    const paid = merged(rows.map(span));
    return { day: d, key: k, rows, shift: merged(rows.filter((e) => e.kind === 'shift').map(span)), visit: merged(rows.filter((e) => e.kind === 'visit').map(span)),
      visits: rows.filter((e) => e.kind === 'visit').length, paid, open: rows.some((e) => !e.clockOut),
      mileage: rows.reduce((t, e) => t + (Number(e.mileage) || 0), 0) };
  });
  let weekReg = 0;
  perDay.forEach((p) => {
    const dt = Math.max(0, p.paid - 12);
    let ot = Math.max(0, Math.min(p.paid, 12) - 8);
    let reg = p.paid - dt - ot;
    const room = Math.max(0, 40 - weekReg);
    if (reg > room) { ot += reg - room; reg = room; }
    weekReg += reg;
    Object.assign(p, { reg, ot, dt });
  });
  const sum = (k) => perDay.reduce((t, p) => t + p[k], 0);
  return { perDay, total: { paid: sum('paid'), shift: sum('shift'), visit: sum('visit'), visits: sum('visits'), reg: sum('reg'), ot: sum('ot'), dt: sum('dt'), mileage: sum('mileage'), open: perDay.some((p) => p.open) } };
};

export const downloadCsv = (name, rows) => {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = name;
  a.click();
};
